<template>
    <div class="tab-clinic-location">
        <div class="blockcont">
            <div class="row">
                <div class="col-xs-12">
                    <div class="smalltitle">
                        Clinic Locations <br/>
                        <span style='line-height: 20px !important;display: block;color: #707070;font-family: "Helvetica Neue",Helvetica,Arial,sans-serif; font-size: 14px; font-weight: normal'>
                                Please update Service Facility information below including NPI and Place of Service code for each location. This information is needed for BOX 32 at the CMS1500 claim form. Those are also shown in the clinic and physician profile for patients to search and book appointment. Default Service code for each location is setup here. It can also be changed when user created a new appointment at the EMR.</span>

                        <br/>
                    </div>
                </div>
            </div>
            <ul class="itemlist" v-for="item in clinicDataAdded">
                <li>
                    <div class="row">
                        <div class="col-xs-5"><strong class="address">{{item.name}}</strong><br>
                            <em>{{item.streetNumber}} {{item.streetName}} #{{item.suite}}, {{item.city}},
                                {{item.state}}</em>
                        </div>
                        <div class="col-xs-3">

                        </div>
                        <div class="col-xs-2" style="text-align: right">
                            <span style="display: inline-block;" class="phone">{{item.phone}}</span>
                        </div>
                        <div class="col-xs-2 itemaction">

                        </div>
                    </div>
                </li>
            </ul>
            <div class="clinic-time-zone">
                <div class="smalltitle">Setup Clinic Time Zone</div>
                <div v-if="loadingTimeZone">Loading clinic time zone...</div>
                <div v-else>
                    <div v-if="timeZoneError" class="text-danger">{{ timeZoneError }}</div>
                    <div v-if="!timeZoneError" class="time-zone-controls">
                        <div class="time-zone-picker" ref="timeZonePicker">
                            <button class="time-zone-trigger" type="button" :disabled="savingTimeZone"
                                    :aria-expanded="timeZoneOpen ? 'true' : 'false'" aria-label="Select clinic time zone"
                                    @click="toggleTimeZonePicker" @keydown.esc="closeTimeZonePicker">
                                <span :class="{ 'time-zone-placeholder': !selectedTimeZone }">{{ selectedTimeZone ? selectedTimeZone.label : 'Select a clinic time zone' }}</span>
                                <span class="time-zone-chevron" aria-hidden="true"></span>
                            </button>
                            <div v-if="timeZoneOpen" class="time-zone-menu" @keydown.esc.stop="closeTimeZonePicker">
                                <input ref="timeZoneSearch" v-model="timeZoneSearch" type="text" class="time-zone-search"
                                       placeholder="Search city or time zone..." aria-label="Search time zones">
                                <div class="time-zone-options">
                                    <button v-for="zone in filteredTimeZones" :key="zone.name" type="button"
                                            class="time-zone-option" :class="{ selected: zone.name === clinicTimeZoneName }"
                                            @click="chooseTimeZone(zone)">
                                        <span>{{ zone.label }}</span><span v-if="zone.name === clinicTimeZoneName" aria-hidden="true">✓</span>
                                    </button>
                                    <div v-if="!filteredTimeZones.length" class="time-zone-empty">No matching time zones</div>
                                </div>
                            </div>
                        </div>
                        <button class="btn btn-default" type="button" :disabled="!selectedTimeZone || savingTimeZone" @click.prevent="saveClinicTimeZone">{{ savingTimeZone ? 'Saving...' : 'Save' }}</button>
                    </div>
                    <div v-if="!timeZoneError" class="time-zone-current">{{ currentTimeZonePrompt }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CommonMixin from './../../../../mixins/general/CommonMixin.vue'
import httpClient from '../../../../client'
import clinicTimeZones from '../../../../../config/clinic-time-zones.json'

export default {
  name: 'TabClinicLocation',
  components: {},
  props: {
    userInfo: {}
  },
  mixins: [CommonMixin],
  data () {
    return {
      clinicDataAdded: [],
      dataTimeZones: clinicTimeZones,
      clinicTimeZoneName: '',
      savedTimeZoneName: '',
      currentTimeZone: -6,
      timeZoneOpen: false,
      timeZoneSearch: '',
      loadingTimeZone: true,
      savingTimeZone: false,
      timeZoneError: ''
    }
  },
  computed: {
    selectedTimeZone () {
      return this.dataTimeZones.find(zone => zone.name === this.clinicTimeZoneName)
    },
    filteredTimeZones () {
      const search = this.timeZoneSearch.trim().toLowerCase()
      return search ? this.dataTimeZones.filter(zone => zone.label.toLowerCase().includes(search) || zone.name.toLowerCase().includes(search)) : this.dataTimeZones
    },
    currentTimeZonePrompt () {
      const selected = this.dataTimeZones.find(zone => zone.name === this.savedTimeZoneName)
      if (selected) return `Current: ${selected.label}`
      const offset = Number(this.currentTimeZone)
      return `Current: UTC${offset >= 0 ? '+' : ''}${offset} — select a region`
    },
    disabledForm () {
      return true
    }
  },
  watch: {},
  updated () {
  },
  mounted () {
    this.getClinicLocation()
    this.getClinicTimeZone()
    document.addEventListener('click', this.onTimeZoneOutsideClick)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onTimeZoneOutsideClick)
  },
  methods: {
    toggleTimeZonePicker () {
      this.timeZoneOpen = !this.timeZoneOpen
      this.timeZoneSearch = ''
      if (this.timeZoneOpen) this.$nextTick(() => this.$refs.timeZoneSearch.focus())
    },
    closeTimeZonePicker () {
      this.timeZoneOpen = false
      this.timeZoneSearch = ''
    },
    chooseTimeZone (zone) {
      this.clinicTimeZoneName = zone.name
      this.closeTimeZonePicker()
    },
    onTimeZoneOutsideClick (event) {
      if (this.timeZoneOpen && this.$refs.timeZonePicker && !this.$refs.timeZonePicker.contains(event.target)) this.closeTimeZonePicker()
    },
    async getClinicTimeZone () {
      this.loadingTimeZone = true
      this.timeZoneError = ''
      try {
        const response = await httpClient.get('/users/clinic-time-zone/' + this.userInfo.id)
        if (response.data.s !== 200) throw new Error(response.data.msg || 'Could not load clinic time zone')
        const config = response.data.data
        this.currentTimeZone = config.timezone
        this.clinicTimeZoneName = this.dataTimeZones.some(zone => zone.name === config.name) ? config.name : ''
        this.savedTimeZoneName = this.clinicTimeZoneName
      } catch (error) {
        this.timeZoneError = error.message
      } finally {
        this.loadingTimeZone = false
      }
    },
    async saveClinicTimeZone () {
      if (!this.selectedTimeZone || this.savingTimeZone) return
      this.savingTimeZone = true
      try {
        const response = await httpClient.put('/users/clinic-time-zone/' + this.userInfo.id, this.selectedTimeZone)
        if (response.data.s !== 200) throw new Error(response.data.msg || 'Could not save clinic time zone')
        this.currentTimeZone = this.selectedTimeZone.timezone
        this.savedTimeZoneName = this.clinicTimeZoneName
        this.alertSuccess(response.data.msg)
      } catch (error) {
        this.alertError(error.message)
      } finally {
        this.savingTimeZone = false
      }
    },
    async getClinicLocation () {
      try {
        let response = await httpClient.get('/users/clinic-location-of-clinic/' + this.userInfo.id)
        if (response.data.s === 200) {
          this.clinicDataAdded = response.data.data.clinicLocations
        } else {
          this.alertError(response.data.msg)
        }
      } catch (e) {
        this.alertError(e.message)
      }
    }
  }
}
</script>
<style scoped="">
    .clinic-time-zone {
        border-top: 1px solid #ddd;
        margin-top: 24px;
        padding-top: 20px;
    }
    .time-zone-controls {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 10px;
    }
    .time-zone-picker {
        position: relative;
        width: min(500px, 100%);
    }
    .time-zone-trigger {
        width: 100%;
        min-height: 42px;
        padding: 9px 13px;
        border: 1px solid #cfd5d9;
        border-radius: 4px;
        background: #fff;
        color: #34404a;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        text-align: left;
        cursor: pointer;
    }
    .time-zone-trigger:hover, .time-zone-trigger:focus {
        border-color: #b3be00;
        outline: none;
        box-shadow: 0 0 0 3px rgba(210, 220, 26, .18);
    }
    .time-zone-placeholder, .time-zone-current { color: #777; }
    .time-zone-current { margin-top: 7px; font-size: 12px; }
    .time-zone-chevron {
        flex: none;
        border: 5px solid transparent;
        border-top-color: #66737c;
        transform: translateY(3px);
    }
    .time-zone-menu {
        position: absolute;
        z-index: 20;
        left: 0;
        right: 0;
        bottom: calc(100% + 5px);
        padding: 8px;
        border: 1px solid #d9dedf;
        border-radius: 5px;
        background: #fff;
        box-shadow: 0 8px 24px rgba(0, 0, 0, .18);
    }
    .time-zone-search {
        box-sizing: border-box;
        width: 100%;
        height: 36px;
        padding: 7px 10px;
        border: 1px solid #d9dedf;
        border-radius: 3px;
        outline-color: #b3be00;
    }
    .time-zone-options { max-height: 220px; overflow-y: auto; margin-top: 6px; }
    .time-zone-option {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        width: 100%;
        padding: 8px 10px;
        border: 0;
        border-radius: 3px;
        background: #fff;
        color: #444;
        text-align: left;
        cursor: pointer;
    }
    .time-zone-option:hover, .time-zone-option:focus, .time-zone-option.selected {
        background: #f4f6d8;
        outline: none;
    }
    .time-zone-empty { padding: 10px; color: #777; }
    @media (max-width: 600px) {
        .time-zone-controls { flex-wrap: wrap; }
        .time-zone-picker { width: 100%; }
    }
</style>
